import { useEffect, useReducer } from 'react';

import { useAuth } from '@quizzach/contexts';
import { IUserQuizzes } from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';

// ------------------------------------------------------------------ //
// Notification center content, frontend-only for now. The single
// notification is the unplayed gratis quiz - the quick quiz gifted on
// registration, i.e. the user's OLDEST quick quiz. `fresh` means never
// opened (backend flips it on the first quiz view), so once the quiz
// is started the notification is gone on the next load; clicking the
// row dismisses it immediately and starts the quiz.
// Future broadcast notifications: add entries in the hook below.
// ------------------------------------------------------------------ //

interface AppNotification {
	id: string;
	/* i18n keys - copy lives in the locale files */
	titleKey: string;
	bodyKey?: string;
	/* click destination; the row renders as a button when set */
	path?: string;
}

// Dev preview toggle - flip while developing to see the notification
// center in either state (you still need to be logged in; the bell and
// the sheet block are auth-gated):
//   'on'   always shows the gratis notification, even if played and even
//          after clicking it (click still navigates: to the real oldest
//          quick quiz when known, otherwise to the profile)
//   'off'  always shows the empty state (»nema novih obavijesti on
//          desktop, nothing at all on mobile)
//   'auto' the real behavior - KEEP it at 'auto' in commits
type DevForceNotification = 'auto' | 'on' | 'off';
const DEV_FORCE_NOTIFICATION = 'auto' as DevForceNotification;

// Module-level store: TopNav and BottomNav both mount the hook, so the
// /api/quizzes fetch runs once per logged-in identity and a dismissal
// in one nav is visible in the other immediately.
let owner: string | null = null;
let gratisPath: string | null = null;
// oldest quick quiz regardless of played-state - only used as the click
// target when DEV_FORCE_NOTIFICATION is 'on'
let oldestQuickPath: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

const fetchGratisQuiz = async (forOwner: string) => {
	try {
		const response = await fetchData<IUserQuizzes[]>(
			`/api/quizzes`,
			{ method: 'GET' },
			{ authRoute: true }
		);
		// identity changed mid-flight - drop the result
		if (owner !== forOwner) return;
		if (response.status >= 300 || !response.data) {
			consoleDev('useNotifications', ConsoleStatus.NET, response.status);
			return;
		}
		const oldestQuick = response.data
			.filter((quiz) => quiz.quiz_type === 'quick')
			.sort((a, b) => a.created_at - b.created_at)[0];
		if (oldestQuick) {
			oldestQuickPath = `/quiz/${oldestQuick.uuid}/mode`;
			if (oldestQuick.fresh) gratisPath = oldestQuickPath;
			emit();
		}
	} catch (err) {
		consoleDev('useNotifications', ConsoleStatus.HTTP, err);
	}
};

const ensureLoaded = (nextOwner: string) => {
	if (owner === nextOwner) return;
	owner = nextOwner;
	gratisPath = null;
	oldestQuickPath = null;
	emit();
	fetchGratisQuiz(nextOwner);
};

const dismissNotification = (id: string) => {
	if (id === 'gratis') {
		gratisPath = null;
		emit();
	}
};

export const useNotifications = () => {
	const { isAuthenticated, profileInfo } = useAuth();
	const [, forceRender] = useReducer((tick: number) => tick + 1, 0);

	useEffect(() => {
		listeners.add(forceRender);
		return () => {
			listeners.delete(forceRender);
		};
	}, []);

	const nickname = profileInfo?.nickname;
	useEffect(() => {
		if (nickname) ensureLoaded(nickname);
	}, [nickname]);

	// '/profile' fallback keeps the forced preview clickable even before
	// (or without) a fetched quiz list
	const activePath =
		DEV_FORCE_NOTIFICATION === 'on'
			? oldestQuickPath ?? gratisPath ?? '/profile'
			: DEV_FORCE_NOTIFICATION === 'off'
				? null
				: gratisPath;

	const notifications: AppNotification[] =
		isAuthenticated && activePath
			? [
					{
						id: 'gratis',
						titleKey: 'nav.notificationGratis',
						path: activePath,
					},
				]
			: [];

	return { notifications, dismissNotification };
};
